import { useInfiniteQuery } from '@tanstack/react-query';
import { api, GuildSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildsOptions {
  search?: string;
  guildType?: string;
  sortBy?: string;
}

interface UseGuildsReturn {
  guilds: GuildSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useGuilds(options: UseGuildsOptions = {}): UseGuildsReturn {
  const filters: Record<string, unknown> = {
    search: options.search || undefined,
    guildType: options.guildType || undefined,
    sortBy: options.sortBy || 'newest',
  };

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.guilds.list(filters),
      queryFn: ({ pageParam = 0 }) =>
        api.listGuilds({
          search: options.search || undefined,
          guildType: options.guildType || undefined,
          sortBy: options.sortBy || 'newest',
          limit: LIMIT,
          offset: pageParam,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
    });

  const guilds = data?.pages.flatMap((page) => page.guilds) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    guilds,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load guilds' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: !!hasNextPage,
  };
}
